import { Card } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { IAddUserFormValues } from '../../../@types/modelV2/rights/user/addUserFormValues';
import { ISqlEmployee } from '../../../@types/modelV2/rights/user/sqlEmployee';
import UserModelHelper from '../../../@types/modelV2/rights/user/userModelHelper';
import { IUserUpsert } from '../../../@types/modelV2/rights/user/userUpsert';
import { useGetUsers, useHandleDeleteUser, useHandleLogoutUsers, useHandleUpsertUser } from '../../../hooks/query/rights/userQueries';
import DateHelper from '../../../services/helper/dateHelper';
import { booleanToYesNo, compareDate } from '../../../services/helper/functionHelper';
import CustomTable from '../../customComponentV2/DataGrid/CustomTable';
import ConfirmationDialog from '../../customComponentV2/dialog/ConfirmationDialog';
import ProjectDialog from '../../customComponentV2/dialog/ProjectDialog';
import Screen from '../../customComponentV2/screen/Screen';
import AddUserDialog from './dialog/AddUserDialog';
import UserEditDialog from './dialog/UserEditDialog';

const iconsLocation = `${ASSET_BASE}/assets/images/icons`;

const UserManager = () : React.ReactElement => {
    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<ISqlEmployee | null>(null);
    const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState<boolean>(false);
    const [deletingUser, setDeletingUser] = useState<ISqlEmployee | null>(null);
    const [logoutUser, setLogoutUser] = useState<ISqlEmployee | null>(null);

    const { isLoading: isLoadingUsers, data: users } = useGetUsers();
    const { isLoading: isDeletingUser, mutate: deleteUser, isSuccess: isUserDeleteSuccessful } = useHandleDeleteUser();
    const { isLoading: isUpsertingUser, mutate: upsertUser, isSuccess: isUserUpsertSuccessful } = useHandleUpsertUser();
    const { isLoading: isLoggingOutAllUsers, mutate: logoutUsers } = useHandleLogoutUsers();

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (isUserDeleteSuccessful) {
            closeDeletePopup();
        }
    }, [isUserDeleteSuccessful]);

    useEffect(() => {
        if (isUserUpsertSuccessful) {
            setIsAddUserDialogOpen(false);
        }
    }, [isUserUpsertSuccessful]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onDeleteUser = () : void => {
        if (!deletingUser) return;
        deleteUser(deletingUser.id);
    };

    const logoutSingleUser = async () : Promise<void> => {
        if (!logoutUser) return;

        logoutUsers([logoutUser.id]);

        setLogoutUser(null);
    };

    const onLogoutAllUsers = () : void => {
        if (!users) return;

        const userIds = users?.map(x => x.id);
        logoutUsers(userIds);
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onSubmit = (upsert : IUserUpsert) : void => {
        upsertUser(upsert);
    };

    const onDelete = (userToDelete : ISqlEmployee) : void => {
        const user = users?.find(x => x.id === userToDelete.id) || null;
        setDeletingUser(user);
    };

    const onEdit = (user : ISqlEmployee) : void => {
        const newUser = users?.find(x => x.id === user.id) || null;

        setIsEditDialogOpen(true);
        setSelectedUser(newUser);
    };

    const onEditDialogClose = () : void => {
        setIsEditDialogOpen(false);
        setSelectedUser(null);
    };

    const onOpenAddUserDialog = () : void => {
        setIsAddUserDialogOpen(true);
    }

    const onCloseAddUserDialog = () : void => {
        setIsAddUserDialogOpen(false);
    }

    const closeDeletePopup = () : void => {
        setDeletingUser(null);
    };

    const closeLogOutUserPopup = () : void => {
        setLogoutUser(null);
    };

    const setSingleUserLogoutItem = (selectedUser : ISqlEmployee) : void => {
        const User = users?.find(x => x.id === selectedUser.id) || null;
        setLogoutUser(User);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const isLoading = useMemo<boolean>(() => {
        return isLoadingUsers
        || isDeletingUser
        || isUpsertingUser
        || isLoggingOutAllUsers;
    }, [
        isLoadingUsers,
        isDeletingUser,
        isUpsertingUser,
        isLoggingOutAllUsers,
    ]);

    const getInitialFormValues = useMemo<IAddUserFormValues>(() => {
        return UserModelHelper.createAddUserFormValues();
    }, []);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen
            useAnimatedLoader    
            isLoading={isLoading}>
            <div className={'fdr wfill jcfe'}>
                {/* <MuiAutocompleteSelect
                    isMulti
                    className={'w500'}
                    name={'rights'}
                    label={'Rights'}
                    options={rightFilterOptions}
                    value={selectedRightOptions}
                    onChange={onSelectedRightOptionsChange}
                /> */}
            </div>
            <Card className={'fdc'}>
                <CustomTable<ISqlEmployee>
                    enableCSVExport
                    enableAdding
                    addFunction={onOpenAddUserDialog}

                    enableEditing
                    editFunction={onEdit}

                    enableDeleting
                    deleteFunction={onDelete}
                    disableDeleteButton={(row : ISqlEmployee) : boolean => !row.isActive}

                    enableDetails={(user : ISqlEmployee) : boolean => user.isActive}
                    detailFunction={setSingleUserLogoutItem}
                    detailTooltip={'Logout User'}
                    detailIcon={<img className={'h20 w20 pr2 pb2'} src={`${iconsLocation}/user_logout.svg`} />}

                    enableSorting
                    enableFiltering
                    fitWidthToPage
                    columns={[
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Email', field: 'email', enableFiltering: true, enableSorting: true },
                        { title: 'Employee Number', field: 'employeeNumber', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', formatFunction: DateHelper.formatDateUtc, sortFunction: compareDate, enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', formatFunction: DateHelper.formatDateUtc, sortFunction: compareDate, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', formatFunction: booleanToYesNo, type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={users ?? []}
                    initialSortOrder={[{ columnName: 'createdOn_Created On', direction: 'desc' }]}
                    isActive={(row : ISqlEmployee) : boolean => row.isActive}
                />
            </Card>
            <ProjectDialog
                title={'Add New User'}
                isOpen={isAddUserDialogOpen}
                onClose={onCloseAddUserDialog}
                isLoadingCircular={isLoading}
                maxWidth={'md'}>
                <AddUserDialog isLoading={isLoading} initialFormValues={getInitialFormValues} onSubmit={onSubmit} onClose={onCloseAddUserDialog} />
            </ProjectDialog>
            <ConfirmationDialog
                open={!!deletingUser}
                isLoading={isLoading}
                title={'Inactivate User'}
                description={'Are you sure you would like to set the following item to inactive? '}
                onAccept={onDeleteUser}
                onClose={closeDeletePopup}
                dialogType={'red'}
            />
            <ConfirmationDialog
                open={!!logoutUser}
                isLoading={isLoading}
                title={'Logout User'}
                description={'Are you sure you want to logout this user?'}
                onAccept={logoutSingleUser}
                onClose={closeLogOutUserPopup}
                dialogType={'red'}
            />
            <ProjectDialog
                title={'Edit User'}
                isOpen={isEditDialogOpen}
                onClose={onEditDialogClose}
                isLoadingCircular={isLoading}
                fullScreen
            >
                <UserEditDialog selectedEmployee={selectedUser} />
            </ProjectDialog>
        </Screen>
    );
};

export default UserManager;